/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { Disclosure } from 'react-aria-components/Disclosure';
import { AccordionContext } from './context';
import styles from './styles.module.css';
import type { AccordionProps } from './types';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';

/**
 * Accordion - A collapsible content component with expandable sections.
 *
 * Provides an accessible accordion interface for organizing content into
 * collapsible sections. Supports both compact and cozy variants with
 * integrated controls for expanding/collapsing content areas.
 *
 * @param props - The accordion props.
 * @param props.ref - Reference to the root div element.
 * @param props.children - Content to render within the accordion, typically AccordionHeader and AccordionPanel.
 * @param props.className - Additional CSS class names for styling.
 * @param props.variant - Visual variant of the accordion ('compact' or 'cozy').
 * @param props.isDisabled - Whether the accordion is disabled.
 * @returns The accordion component.
 *
 * @example
 * ```tsx
 * // Basic accordion
 * <Accordion>
 *   <AccordionHeader>
 *     <AccordionTrigger>Section Title</AccordionTrigger>
 *   </AccordionHeader>
 *   <AccordionPanel>Content goes here</AccordionPanel>
 * </Accordion>
 * ```
 *
 * @example
 * ```tsx
 * // Compact variant
 * <Accordion variant="compact">
 *   <AccordionHeader>
 *     <AccordionTrigger>Compact Section</AccordionTrigger>
 *   </AccordionHeader>
 *   <AccordionPanel>Compact content</AccordionPanel>
 * </Accordion>
 * ```
 *
 * @example
 * ```tsx
 * // Multiple accordions in a group
 * <AccordionGroup>
 *   <Accordion>
 *     <AccordionHeader>
 *       <AccordionTrigger>First Section</AccordionTrigger>
 *     </AccordionHeader>
 *     <AccordionPanel>First content</AccordionPanel>
 *   </Accordion>
 *   <Accordion>
 *     <AccordionHeader>
 *       <AccordionTrigger>Second Section</AccordionTrigger>
 *     </AccordionHeader>
 *     <AccordionPanel>Second content</AccordionPanel>
 *   </Accordion>
 * </AccordionGroup>
 * ```
 */
export function Accordion({ ref, ...props }: AccordionProps) {
  [props, ref] = useContextProps(props, ref ?? null, AccordionContext);

  const { children, className, variant = 'cozy', isDisabled, ...rest } = props;

  return (
    <AccordionContext.Provider
      value={{
        variant,
        isDisabled,
      }}
    >
      <Disclosure
        {...rest}
        className={composeRenderProps(className, (className) =>
          clsx('group/accordion', styles.accordion, className),
        )}
        isDisabled={isDisabled}
      >
        {children}
      </Disclosure>
    </AccordionContext.Provider>
  );
}
